import usePageTitle from '../hooks/usePageTitle';
import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplets } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { clientsService } from '../services';
import { User, Client } from '../types';

const Onboarding: React.FC = () => {
  usePageTitle('Onboarding');
  const { currentUser, setCurrentUser, needsOnboarding } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [client, setClient] = useState<Client | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Prefill the name input from whatever the JWT upsert seeded; users
  // will usually replace the email-prefix default with their real name.
  useEffect(() => {
    setName(currentUser?.name ?? '');
  }, [currentUser?.id, currentUser?.name]);

  // Non-consultants get role-specific welcome copy that names their org.
  useEffect(() => {
    let cancelled = false;
    if (!currentUser?.client_id) {
      setClient(null);
      return;
    }
    clientsService
      .getClientById(currentUser.client_id)
      .then((c) => {
        if (!cancelled) setClient(c);
      })
      .catch(() => {
        // Not fatal — the page just shows generic copy without the org name.
        if (!cancelled) setClient(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.client_id]);

  // If a backfilled user lands here directly, send them home.
  useEffect(() => {
    if (currentUser && !needsOnboarding) {
      navigate('/', { replace: true });
    }
  }, [currentUser, needsOnboarding, navigate]);

  if (!currentUser) {
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      showError('Name cannot be empty');
      return;
    }
    setSubmitting(true);
    try {
      const updated = await apiService.post<User>(
        '/auth/complete-onboarding',
        { name: trimmed },
      );
      setCurrentUser(updated);
      // PrivateRoute hops non-consultants to /portal automatically.
      navigate('/', { replace: true });
    } catch (err) {
      showError(
        'Could not finish onboarding',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  let welcome: React.ReactNode;
  if (currentUser.role === 'h2okay_admin') {
    welcome = "You'll manage every consultancy in the H2Okay system.";
  } else if (currentUser.role === 'consultant') {
    welcome = "You'll see every client and facility in the H2Okay system.";
  } else if (currentUser.role === 'client') {
    welcome = client ? (
      <>You'll see facilities and inspections for <strong>{client.name}</strong>.</>
    ) : (
      "You'll see facilities and inspections for your organization."
    );
  } else {
    welcome = client ? (
      <>You're a facility manager at <strong>{client.name}</strong>.</>
    ) : (
      "You'll manage facilities for your organization."
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--background, #f6f8fb)',
        padding: '2rem',
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 480,
          width: '100%',
          padding: '2rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem',
          }}
        >
          <Droplets size={28} color="var(--primary-blue, #0077be)" />
          <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Welcome to H2Okay</h1>
        </div>

        <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>{welcome}</p>

        <form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
          <label htmlFor="onboarding-name" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
            What should we call you?
          </label>
          <input
            id="onboarding-name"
            className="form-input"
            type="text"
            required
            autoFocus
            maxLength={255}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            style={{ width: '100%', marginBottom: '0.25rem' }}
          />
          <div
            style={{
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              marginBottom: '1.25rem',
            }}
          >
            You can change this later from your profile.
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !name.trim()}
            style={{ width: '100%' }}
          >
            {submitting ? 'Saving…' : `Continue as ${name.trim()}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Onboarding;
